import type { DocumentFont } from '@flobi/artwork-core';
import type { CanvasHandle } from '../editor/useEditorCanvas';
import { chooseFonts } from '../editor/imageTools/document';
import { fontFinder } from '../services/fontFinder';
import type { LayerFile } from './layerFile';

type Canvas = Pick<CanvasHandle, 'placeLayerDocument' | 'revealGenerationTarget' | 'viewKey'>;

/**
 * Until the canvas is drawn, the editor's handle is a stand-in whose every call does nothing and
 * answers null (useEditorCanvas's first handleRef). The file opens a moment before that, so a
 * layered file placed as the file opened was "could not be placed" (Tarek, 29 Oct). The real
 * canvas always says where its view is; the stand-in says ''. Waits for it, at most `ms`.
 */
export async function canvasReady(canvas: Pick<CanvasHandle, 'viewKey'>, ms = 20_000, step = 100): Promise<boolean> {
  for (let waited = 0; ; waited += step) {
    if (canvas.viewKey() !== '') return true;
    if (waited >= ms) return false;
    await new Promise((resolve) => setTimeout(resolve, step));
  }
}

/** The pause between a whole send's artboards: the tab draws, and each one goes out to the room before the next. */
const BETWEEN_BOARDS_MS = 40;

/**
 * A layered file from a chat placed in the open file, as Layers with Live Text lands (landTool's
 * `document`): its faces put in the file first, then one new artboard the picture's size beside the
 * others, every layer on it. One undo step. Answers the board, or throws with a sentence.
 *
 * A whole send (`file.whole`) is one artboard per group, each its own undo step, one at a time. If one is refused the ones
 * already placed stay, and `placed` says how many went: the address is off the page by then, so nothing is placed twice.
 */
export async function placeLayerFile(canvas: Canvas, file: LayerFile, documentId: string, find = fontFinder, wait = canvasReady, pause = BETWEEN_BOARDS_MS): Promise<{ board: string; swapped: { from: string; to: string }[]; placed: number; total: number }> {
  const finder = await find(documentId);
  if (!(await wait(canvas))) throw Error('The file took too long to open, so the layers were not placed. Open the layers again.');
  const all = [file, ...(file.whole?.more ?? [])];
  const swapped = new Map<string, { from: string; to: string }>();
  let first: string | null = null;
  let placed = 0;
  for (const one of all) {
    const chosen = await chooseFonts<DocumentFont>(one.document, finder.resolve, finder.preferred);
    for (const swap of chosen.swapped) swapped.set(swap.from, swap);
    const board = canvas.placeLayerDocument('', chosen.document, one.pictures, chosen.fonts, one.size, one.board);
    if (!board) break;
    first ??= board;
    placed += 1;
    if (placed < all.length && pause > 0) await new Promise((resolve) => setTimeout(resolve, pause));
  }
  if (!first) throw Error('The layers could not be placed in this file.');
  canvas.revealGenerationTarget(first);
  return { board: first, swapped: [...swapped.values()], placed, total: all.length };
}

/** What landed, for the editor's message. */
export function placedWords(file: LayerFile, placed: number, total: number): string {
  if (!file.whole) return `The layers are on a new artboard${file.texts ? ', with the text editable' : ''}.`;
  const { pictures, texts } = file.whole;
  const what = [pictures ? `${pictures} ${pictures === 1 ? 'picture' : 'pictures'}` : '', texts ? `${texts} ${texts === 1 ? 'text' : 'texts'}` : ''].filter(Boolean).join(' and ');
  if (placed < total) return `Only ${placed} of ${total} artboards could be placed.`;
  return `${what} ${pictures + texts === 1 ? 'is' : 'are'} on ${total === 1 ? 'a new artboard' : `${total} new artboards`}.`;
}
